import React from 'react';
import { TrendingUp, Target, Shield, RefreshCw, ArrowDown, Zap, Activity } from 'lucide-react';

/**
 * ProgressionBadge - Phase 4.4
 * 
 * Displays exercise progression information in a motivating, premium style.
 * All badges use BODYMAX brand neon green (#CCFF00) for consistent branding.
 * 
 * Progression Actions:
 * - increase_weight: "Ready to Progress"
 * - increase_rep: "Rep Target Up"
 * - hold_load: "Lock It In"
 * - rotate_variation: "Fresh Variation"
 * - reduce_load: "Reset Stronger"
 * - calibration: "Getting to Know You"
 * - deload: "Focus Week"
 */

// BODYMAX brand progression badge styles
// Dark mode: dark olive bg with subtle lime glow and lime accent text
// Light mode: accent blue bg with white text for clean contrast
const brandGreen = {
  bg: 'bg-accent dark:bg-accent/15',
  border: 'border-accent/30 dark:border-accent/25',
  text: 'text-white dark:text-accent',
  subtext: 'text-white/70 dark:text-muted-foreground',
};

const progressionStyles = {
  increase_weight: {
    ...brandGreen,
    icon: TrendingUp,
  },
  increase_rep: {
    ...brandGreen,
    icon: Target,
  },
  hold_load: {
    ...brandGreen,
    icon: Shield,
  },
  rotate_variation: {
    ...brandGreen,
    icon: RefreshCw,
  },
  reduce_load: {
    ...brandGreen,
    icon: ArrowDown,
  },
  calibration: {
    ...brandGreen,
    icon: Activity,
  },
  deload: {
    ...brandGreen,
    icon: Zap,
  },
  default: {
    ...brandGreen,
    icon: Activity,
  },
};

export const ProgressionBadge = ({ progression, compact = false }) => {
  if (!progression || !progression.action) {
    return null;
  }

  const style = progressionStyles[progression.action] || progressionStyles.default;
  const Icon = style.icon;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-accent/15 border border-accent/25">
        <Icon className="w-4 h-4 text-accent" />
        <span className="text-sm font-heading font-bold text-accent uppercase tracking-wider">
          {progression.label}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg ${style.bg} ${style.border} border mt-2`}>
      <div className="flex items-center justify-center w-6 h-6 rounded-md bg-white/20 dark:bg-accent-deep shrink-0">
        <Icon className={`w-3.5 h-3.5 ${style.text}`} />
      </div>
      <div className="flex flex-col gap-0.5">
        <span className={`text-xs font-semibold ${style.text} uppercase tracking-wider leading-none`}>
          {progression.label}
        </span>
        <span className={`text-xs ${style.subtext || 'text-muted-foreground'} leading-tight`}>
          {progression.display_text}
        </span>
      </div>
    </div>
  );
};

/**
 * ProgressionSummary - Shows next workout adjustments after completion
 */
export const ProgressionSummary = ({ exercises }) => {
  if (!exercises || exercises.length === 0) {
    return null;
  }

  // Filter to meaningful progressions
  const meaningfulProgressions = exercises.filter(ex => 
    ex.progression && 
    ex.progression.action && 
    !['calibration', 'deload'].includes(ex.progression.action)
  ).slice(0, 5);

  if (meaningfulProgressions.length === 0) {
    return (
      <div className="mt-3 p-3.5 bg-card rounded-xl border border-border">
        <h4 className="text-xs font-semibold text-muted-foreground mb-1.5 flex items-center gap-2 uppercase tracking-wider">
          <Zap className="w-3.5 h-3.5 text-accent" />
          Next Workout
        </h4>
        <p className="text-xs text-muted-foreground">
          Solid work — keep the same loads and own the reps.
        </p>
      </div>
    );
  }

  const getProgressionArrow = (action) => {
    switch (action) {
      case 'increase_weight':
        return '↑';
      case 'increase_rep':
        return '+';
      case 'reduce_load':
        return '↓';
      case 'rotate_variation':
        return '⟳';
      default:
        return '→';
    }
  };

  const getShortText = (ex) => {
    const prog = ex.progression;
    switch (prog.action) {
      case 'increase_weight':
        return prog.suggested_weight_kg ? `+${(prog.suggested_weight_kg * 0.025).toFixed(1)} kg` : 'increase weight';
      case 'increase_rep':
        return '+1 rep';
      case 'hold_load':
        return 'hold load';
      case 'reduce_load':
        return 'reduce load';
      case 'rotate_variation':
        return 'new variation';
      default:
        return prog.display_text || 'maintain';
    }
  };

  return (
    <div className="mt-3 p-3.5 bg-card rounded-xl border border-border">
      <h4 className="text-xs font-semibold text-foreground mb-2.5 flex items-center gap-2 uppercase tracking-wider">
        <TrendingUp className="w-3.5 h-3.5 text-accent" />
        Next Workout
      </h4>
      <div className="space-y-2">
        {meaningfulProgressions.map((ex, idx) => {
          const style = progressionStyles[ex.progression.action] || progressionStyles.default;
          return (
            <div key={idx} className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground truncate max-w-[60%]">{ex.exercise}</span>
              <span className={`${style.text} text-xs font-mono font-bold flex items-center gap-1`}>
                <span>{getProgressionArrow(ex.progression.action)}</span>
                <span>{getShortText(ex)}</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * RecentAdaptations - Shows recent progression changes on progress page
 */
export const RecentAdaptations = ({ adaptations }) => {
  if (!adaptations || adaptations.length === 0) {
    return null;
  }

  return (
    <div className="p-3.5 bg-card rounded-xl border border-border">
      <h4 className="text-xs font-semibold text-foreground mb-2.5 flex items-center gap-2 uppercase tracking-wider">
        <Activity className="w-3.5 h-3.5 text-accent" />
        Recent Adaptations
      </h4>
      <p className="text-xs text-muted-foreground mb-2.5">
        Adjusting based on your performance
      </p>
      <div className="space-y-2">
        {adaptations.slice(0, 5).map((adapt, idx) => {
          const style = progressionStyles[adapt.action] || progressionStyles.default;
          const Icon = style.icon;
          return (
            <div key={idx} className="flex items-center gap-2">
              <Icon className={`w-3 h-3 ${style.text}`} />
              <span className="text-xs text-muted-foreground truncate flex-1">{adapt.exercise}</span>
              <span className={`${style.text} text-xs font-mono font-bold`}>
                {adapt.shortText}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressionBadge;
